/**
 * leadToUniBoxMapper — the bridge between schema-accurate N8N data
 * (CampaignMetrics / LeadCardResponse) and the UniBox render shape
 * (UniBoxGroup / UniBoxEntity).
 *
 * This is the "backend mapping (next phase)" the dummyData header comment
 * anticipated: instead of hand-authoring `UniBoxEntity` rows, we derive them
 * from the real lead-card schema. The status mapping collapses N8N's 8 status
 * values into the 6-key global funnel (taxonomy.ts) the Sales Kanban renders.
 */

import type { CampaignMetrics, LeadCardResponse } from '../../types/n8nResponses';
import type {
  UniBoxEntity,
  UniBoxGroup,
  UniBoxSequenceStep,
  UniBoxChannelProgress,
  UniBoxNextSend,
  UniBoxControlState,
} from '../../components/UniBox/types';
import { SALES_SUBSTATUS_BY_STAGE } from '../../components/UniBox/taxonomy';
import type { N8nLeadStatus } from './generators/leadCardGenerator';
import { fill, OUTREACH_BODIES, REPLIES } from './dictionaries';

// ---------------------------------------------------------------------------
// N8N lead status → global UniBox funnel key (taxonomy.ts LeadStatusKey)
// ---------------------------------------------------------------------------

type FunnelKey = 'leads' | 'enriched' | 'outreaching' | 'outreached' | 'engaged' | 'proposal' | 'converted' | 'discarded';

const STATUS_TO_FUNNEL: Record<N8nLeadStatus, FunnelKey> = {
  new_lead: 'leads',
  enriching: 'leads', // pre-enrichment stays in the Lead Pool column
  enriched: 'enriched',
  outreaching: 'outreaching',
  outreached: 'outreached', // OUTREACHED is now its own funnel column
  engaged: 'engaged',
  proposal: 'proposal',
  converted: 'converted',
  discarded: 'discarded',
};

const substatusKeyFor = (funnel: FunnelKey, card: LeadCardResponse): string => {
  // The DB substatus (when the lead carries one) always wins.
  const dbSubstatus = (card as any).lead_substatus || (card as any).substatus;
  if (typeof dbSubstatus === 'string' && dbSubstatus.trim()) {
    const normalized = dbSubstatus.trim().toLowerCase().replace(/[\s-]+/g, '_');
    const vocab = SALES_SUBSTATUS_BY_STAGE[funnel] || [];
    if (vocab.some((def) => def.key === normalized)) return normalized;
  }
  const vocab = SALES_SUBSTATUS_BY_STAGE[funnel];
  if (!vocab || vocab.length === 0) return '';
  // Map status nuance onto the substatus vocabulary deterministically.
  switch (funnel) {
    case 'leads':
      return 'ready';
    case 'enriched':
      return 'ready';
    case 'outreaching':
      // No DB substatus → the stage's entry substatus, "Not started".
      return 'not_started';
    case 'outreached':
      return 'awaiting';
    case 'engaged':
      // Qualified only when ICP is high; otherwise in-conversation.
      return card.icp_score >= 85 ? 'qualified' : 'in_conversation';
    case 'proposal':
      return card.icp_score >= 90 ? 'review_scheduled' : 'proposal_prepared';
    case 'converted':
      return card.icp_score >= 90 ? 'closed_won' : 'onboarding';
    case 'discarded': {
      if (card.icp_fit === 'poor') return 'out_of_icp';
      const seed = parseInt(card.lead_id, 10) || 0;
      const opts = ['not_interested', 'wrong_person', 'bad_timing', 'no_response', 'bounced'];
      return opts[seed % opts.length];
    }
    default:
      return '';
  }
};

// ---------------------------------------------------------------------------
// Sequence building (mirrors dummyData.ts seq() pattern, but derived)
// ---------------------------------------------------------------------------

const channelFromLead = (card: LeadCardResponse): string[] => {
  const channels: string[] = [];
  if (card.person_email) channels.push('email');
  if (card.linkedin_id) channels.push('linkedin');
  if (card.whatsapp_id) channels.push('whatsapp');
  if (card.phone_id) channels.push('call');
  return channels;
};

/** Build per-channel sequence progress + step bodies for an outreaching lead. */
const buildSequence = (
  card: LeadCardResponse,
  channels: string[],
): { progress: UniBoxChannelProgress[]; steps: UniBoxSequenceStep[] } => {
  // How many of the 3 touches are sent, derived from the lead's funnel nuance.
  const sent =
    card.status === 'outreached' ? 3
    : card.status === 'outreaching' ? (card.icp_score > 70 ? 2 : 1)
    : 0;

  const vars = {
    firstName: card.person_name.split(' ')[0] || 'there',
    company: card.company_name || 'your company',
    industry: card.company_industry || 'your industry',
    trigger: 'expansion', proof: 'peer proof',
  };

  const progress: UniBoxChannelProgress[] = [];
  const steps: UniBoxSequenceStep[] = [];
  const baseDay = 26; // matches the dummy-data send-window
  channels.forEach((channel) => {
    progress.push({ channel, total: 3, sent, nextStep: sent >= 3 ? null : sent + 1 });
    for (let i = 0; i < 3; i++) {
      const stepNum = i + 1;
      const isSent = stepNum <= sent;
      steps.push({
        step: stepNum,
        channel,
        subject: channel === 'email' && stepNum === 1 ? `Quick idea for ${vars.company}'s outbound` : undefined,
        body: channel === 'email' ? fill(OUTREACH_BODIES[i % OUTREACH_BODIES.length], vars) : `Hi ${vars.firstName}, touch ${stepNum} on ${channel}.`,
        sentAt: isSent ? `2026-06-${String(baseDay - (3 - stepNum)).padStart(2, '0')}T10:00:00Z` : null,
      });
    }
  });

  return { progress, steps };
};

// ---------------------------------------------------------------------------
// Entity + group mapping
// ---------------------------------------------------------------------------

const toIsoDaysAgo = (days: number): string => {
  const base = new Date('2026-07-01T12:00:00Z').getTime();
  return new Date(base - days * 86_400_000).toISOString();
};

const toIsoDaysAhead = (days: number): string => {
  const base = new Date('2026-07-01T12:00:00Z').getTime();
  return new Date(base + days * 86_400_000).toISOString();
};

const controlStateFor = (card: LeadCardResponse): UniBoxControlState => {
  if (card.status === 'engaged' && card.icp_score >= 85) return 'human_takeover';
  if (card.status === 'discarded') return 'escalated';
  return 'ai_running';
};

export const mapLeadCardToUniBoxEntity = (
  card: LeadCardResponse,
): UniBoxEntity => {
  const funnel = STATUS_TO_FUNNEL[card.status as N8nLeadStatus] || 'leads';
  const channels = channelFromLead(card);
  const isOutreaching = funnel === 'outreaching';
  const sequence = isOutreaching ? buildSequence(card, channels) : { progress: [], steps: [] };

  // Primary channel's next pending send (for the Kanban card + calendar lens).
  const nextSend: UniBoxNextSend | null = isOutreaching && channels.length > 0
    ? {
        channel: channels[0],
        sequence_step: (sequence.progress[0]?.nextStep) || 1,
        scheduled_at: toIsoDaysAhead(1),
        preview: sequence.steps.find((s) => s.channel === channels[0] && s.sentAt === null)?.body.slice(0, 80) || '',
      }
    : null;

  const seed = parseInt(card.lead_id, 10) || 0;
  const needsReply = funnel === 'engaged' && seed % 2 === 0;
  const lastSnippet = funnel === 'engaged'
    ? REPLIES[seed % REPLIES.length]
    : funnel === 'outreaching'
      ? 'Sequence running — last touch sent.'
      : funnel === 'proposal'
        ? 'Proposal prepared — next action scheduled.'
      : funnel === 'converted'
        ? 'Demo booked for Thursday.'
        : funnel === 'discarded'
          ? 'Marked out of ICP.'
          : 'Awaiting enrichment.';

  return {
    lvl1_id: (card.lvl1_person?.id as number) ?? parseInt(card.lead_id, 10),
    lvl2_id: parseInt(card.lead_id, 10),
    client_name: card.person_name,
    company: card.company_name || undefined,
    avatar: null,
    // Company logo — resolved upstream (storage URL or company domain). Forwarded
    // so the Kanban compact card can render it on the right side of the card.
    company_logo: card.company_logo ?? null,
    company_website: card.company_website ?? null,
    campaign_id: card.campaign_id,
    status: funnel,
    substatus: substatusKeyFor(funnel, card),
    outcome: funnel === 'discarded' ? 'not_interested' : null,
    control_state: controlStateFor(card),
    escalation_reason: funnel === 'discarded' ? 'Out of ICP / no response' : null,
    channels,
    last_message_snippet: lastSnippet,
    last_activity_at: toIsoDaysAgo(seed % 14),
    needs_reply: needsReply,
    next_send: nextSend,
    timezone: (card.lvl1_person as any)?.timezone || null,
    sequenceProgress: sequence.progress.length ? sequence.progress : undefined,
    sequenceSteps: sequence.steps.length ? sequence.steps : undefined,
    // Opportunity value only once qualified (mirrors the dummy-data convention).
    opportunity_value: funnel === 'engaged' && substatusKeyFor(funnel, card) === 'qualified'
      ? 12000 + (seed % 50) * 1000
      : funnel === 'proposal'
        ? 18000 + (seed % 55) * 1000
      : funnel === 'converted'
        ? 24000 + (seed % 60) * 1000
        : null,
    // Extra card-level fields surfaced on the entity for the Kanban card.
    icp_score: card.icp_score,
    icp_fit: card.icp_fit,
    person_role: card.person_role,
    company_industry: card.company_industry,
    country: card.country,
    city: card.city,
    created_at: card.created_at,
  } as UniBoxEntity;
};

export const mapCampaignToUniBoxGroup = (
  campaign: CampaignMetrics,
  leads: LeadCardResponse[],
): UniBoxGroup => {
  const entities = leads.map(mapLeadCardToUniBoxEntity);
  const needsHuman = entities.filter((e) => e.needs_reply || e.control_state === 'human_takeover').length;
  return {
    id: campaign.campaign_id,
    name: campaign.campaign_name,
    automation_mode: campaign.status === 'active' ? 'autopilot' : 'copilot',
    total: campaign.total_leads,
    needs_human: needsHuman,
    status: campaign.status === 'completed' ? 'archived' : (campaign.status as any),
    start: campaign.start_date || campaign.start || null,
    done: campaign.end_date || campaign.done || null,
    created: campaign.created,
    search_location: campaign.search_location,
    primary_campaign_objective: campaign.primary_campaign_objective,
    seller_lvl1: campaign.seller_lvl1,
    seller_name: campaign.seller_name,
  } as UniBoxGroup;
};
